import { useId, useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import Fade from '@mui/material/Fade';
import Tabs from '@mui/material/Tabs';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Portal from '@mui/material/Portal';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';

import { paths } from 'src/routes/paths';
import { usePathname } from 'src/routes/hooks';

import { Iconify } from 'src/components/iconify';
import { AppDialogActions } from 'src/components/custom-dialog';

import { usePwa } from './pwa-context';
import { PWA_INSTALL_GUIDES, detectPwaInstallPlatform } from './pwa-install-guides';

// ----------------------------------------------------------------------

const INSTALL_PROMPT_DELAY_MS = 15000;
const INSTALL_PROMPT_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const INSTALL_PROMPT_SNOOZED_UNTIL = 'rado-pwa-install-prompt-snoozed-until';
const UPDATE_PROMPT_SNOOZE_MS = 24 * 60 * 60 * 1000;
const UPDATE_PROMPT_SNOOZED_UNTIL = 'rado-pwa-update-prompt-snoozed-until';

const getInstallPromptSnooze = () => {
  try {
    return Number(window.localStorage.getItem(INSTALL_PROMPT_SNOOZED_UNTIL) || 0);
  } catch (error) {
    return 0;
  }
};

const setInstallPromptSnooze = (value) => {
  try {
    if (value) {
      window.localStorage.setItem(INSTALL_PROMPT_SNOOZED_UNTIL, String(value));
    } else {
      window.localStorage.removeItem(INSTALL_PROMPT_SNOOZED_UNTIL);
    }
  } catch (error) {
    // نصب همچنان کار می‌کند؛ فقط زمان یادآوری در مرورگرهای محدودشده ذخیره نمی‌شود.
  }
};

const getUpdatePromptSnooze = () => {
  try {
    return Number(window.localStorage.getItem(UPDATE_PROMPT_SNOOZED_UNTIL) || 0);
  } catch (error) {
    return 0;
  }
};

const setUpdatePromptSnooze = (value) => {
  try {
    if (value) {
      window.localStorage.setItem(UPDATE_PROMPT_SNOOZED_UNTIL, String(value));
    } else {
      window.localStorage.removeItem(UPDATE_PROMPT_SNOOZED_UNTIL);
    }
  } catch (error) {
    // به‌روزرسانی همچنان در دسترس است؛ فقط زمان یادآوری ذخیره نمی‌شود.
  }
};

export function PwaPrompts() {
  const pathname = usePathname();
  const {
    applyUpdate,
    installable,
    isApplyingUpdate,
    isStandalone,
    promptInstall,
    updateAvailable,
  } = usePwa();
  const [guideOpen, setGuideOpen] = useState(false);
  const [platform, setPlatform] = useState(detectPwaInstallPlatform);
  const [installOpen, setInstallOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);

  useEffect(() => {
    const isDashboardPage = pathname.startsWith(paths.dashboard.root);
    const isInstallPage = pathname === paths.dashboard.installApp;
    const canOfferInstall = installable || platform === 'ios';

    if (!isDashboardPage || isInstallPage || !canOfferInstall || isStandalone || updateAvailable) {
      setInstallOpen(false);
      return undefined;
    }

    const snoozedUntil = getInstallPromptSnooze();
    if (snoozedUntil > Date.now()) return undefined;

    const timer = window.setTimeout(() => setInstallOpen(true), INSTALL_PROMPT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [installable, isStandalone, pathname, platform, updateAvailable]);

  useEffect(() => {
    if (!updateAvailable) {
      setUpdateOpen(false);
      return;
    }

    setUpdateOpen(getUpdatePromptSnooze() <= Date.now());
  }, [updateAvailable]);

  const dismissUpdate = () => {
    setUpdatePromptSnooze(Date.now() + UPDATE_PROMPT_SNOOZE_MS);
    setUpdateOpen(false);
  };

  const dismissInstall = () => {
    setInstallPromptSnooze(Date.now() + INSTALL_PROMPT_SNOOZE_MS);
    setInstallOpen(false);
    setGuideOpen(false);
  };

  const install = async () => {
    const result = await promptInstall();

    if (result.outcome === 'accepted') {
      setInstallOpen(false);
      setGuideOpen(false);
      setInstallPromptSnooze(0);
      return;
    }

    if (result.outcome === 'unavailable') {
      setInstallOpen(false);
      setGuideOpen(true);
      return;
    }

    dismissInstall();
  };

  const openGuide = () => {
    setInstallOpen(false);
    setGuideOpen(true);
  };

  const manualInstall = !installable;

  return (
    <Portal>
      {updateAvailable && updateOpen && (
        <PromptPosition>
          <PromptCard
            icon="solar:refresh-circle-bold-duotone"
            eyebrow="به‌روزرسانی برنامه"
            title="نسخه جدید رادو آماده است"
            description="نسخه تازه شامل بهبودهای تجربه کاربری، پایداری و عملکرد سامانه است."
            hint="پس از نصب، رادو به‌صورت خودکار بازخوانی می‌شود."
            badge="آماده نصب"
            primaryLabel={isApplyingUpdate ? 'در حال نصب…' : 'به‌روزرسانی اکنون'}
            primaryIcon="solar:restart-bold"
            primaryDisabled={isApplyingUpdate}
            primaryLoading={isApplyingUpdate}
            onPrimary={applyUpdate}
            secondaryLabel="بعداً"
            onSecondary={dismissUpdate}
            onClose={dismissUpdate}
          />
        </PromptPosition>
      )}

      {installOpen && !updateAvailable && (
        <PromptPosition>
          <PromptCard
            icon="solar:smartphone-2-bold-duotone"
            eyebrow="نسخه قابل نصب"
            title={
              manualInstall ? 'رادو را به صفحه اصلی اضافه کنید' : 'رادو را روی دستگاه نصب کنید'
            }
            description="دسترسی سریع‌تر، اجرای تمام‌صفحه و تجربه‌ای شبیه برنامه‌های بومی؛ بدون مراجعه دوباره به مرورگر."
            hint="نصب سریع است و فضای کمی اشغال می‌کند."
            badge="پیشنهاد رادو"
            primaryLabel={manualInstall ? 'مشاهده راهنمای نصب' : 'نصب رادو'}
            primaryIcon={
              manualInstall
                ? 'solar:notebook-minimalistic-bold'
                : 'solar:download-minimalistic-bold'
            }
            onPrimary={manualInstall ? openGuide : install}
            secondaryLabel={manualInstall ? 'بعداً' : 'راهنمای نصب'}
            onSecondary={manualInstall ? dismissInstall : openGuide}
            onClose={dismissInstall}
          />
        </PromptPosition>
      )}

      <InstallGuideDialog
        open={guideOpen}
        platform={platform}
        installable={installable}
        onClose={dismissInstall}
        onInstall={install}
        onPlatformChange={setPlatform}
      />
    </Portal>
  );
}

function InstallGuideDialog({ open, platform, installable, onClose, onInstall, onPlatformChange }) {
  const currentGuide = PWA_INSTALL_GUIDES[platform];

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose} aria-labelledby="pwa-guide-title">
      <DialogTitle id="pwa-guide-title" sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 52,
              height: 52,
              flexShrink: 0,
              display: 'grid',
              borderRadius: 2,
              placeItems: 'center',
              color: 'primary.dark',
              bgcolor: 'primary.lighter',
            }}
          >
            <Iconify icon="solar:smartphone-update-bold-duotone" width={29} />
          </Box>

          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <Typography variant="h5">راهنمای نصب رادو</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
              بدون خروج از صفحه، روش مناسب دستگاه خود را ببینید.
            </Typography>
          </Box>

          <IconButton onClick={onClose} aria-label="بستن راهنمای نصب">
            <Iconify icon="mingcute:close-line" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Alert
          severity="info"
          variant="soft"
          icon={<Iconify icon="solar:magic-stick-3-bold-duotone" />}
          sx={{ mb: 2.5 }}
        >
          راهنمای مناسب دستگاه شما به‌صورت خودکار انتخاب شده است؛ در صورت نیاز دستگاه دیگری را
          انتخاب کنید.
        </Alert>

        <Tabs
          value={platform}
          onChange={(event, value) => onPlatformChange(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 3 }}
        >
          {Object.entries(PWA_INSTALL_GUIDES).map(([value, guide]) => (
            <Tab
              key={value}
              value={value}
              label={guide.label}
              icon={<Iconify icon={guide.icon} />}
              iconPosition="start"
            />
          ))}
        </Tabs>

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              flexShrink: 0,
              display: 'grid',
              borderRadius: 1.5,
              placeItems: 'center',
              color: 'primary.main',
              bgcolor: 'background.neutral',
            }}
          >
            <Iconify icon={currentGuide.icon} width={27} />
          </Box>
          <Box>
            <Typography variant="h6">{currentGuide.title}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {currentGuide.description}
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={1.5}>
          {currentGuide.steps.map((step, index) => (
            <Stack
              key={step.title}
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'background.neutral',
                border: (theme) => `solid 1px ${theme.vars.palette.divider}`,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  display: 'grid',
                  borderRadius: 1.25,
                  placeItems: 'center',
                  color: 'primary.main',
                  bgcolor: 'background.paper',
                }}
              >
                <Iconify icon={step.icon} width={23} />
              </Box>

              <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                <Typography variant="subtitle1">{step.title}</Typography>
                <Typography variant="body2" sx={{ mt: 0.25, color: 'text.secondary' }}>
                  {step.description}
                </Typography>
              </Box>

              <Chip
                size="small"
                color="primary"
                variant="soft"
                label={new Intl.NumberFormat('fa-IR').format(index + 1)}
                sx={{ flexShrink: 0 }}
              />
            </Stack>
          ))}
        </Stack>
      </DialogContent>

      <AppDialogActions>
        {installable && (
          <Button
            variant="contained"
            onClick={onInstall}
            startIcon={<Iconify icon="solar:download-minimalistic-bold" />}
          >
            نصب همین حالا
          </Button>
        )}

        <Button color="inherit" variant="outlined" onClick={onClose}>
          بعداً
        </Button>
      </AppDialogActions>
    </Dialog>
  );
}

function PromptPosition({ children }) {
  return (
    <Box
      sx={{
        left: { xs: 12, sm: 24 },
        bottom: { xs: 'calc(12px + env(safe-area-inset-bottom))', sm: 24 },
        position: 'fixed',
        width: { xs: 'calc(100% - 24px)', sm: 480 },
        maxWidth: { xs: 'calc(100vw - 24px)', sm: 'calc(100vw - 48px)' },
        zIndex: (theme) => theme.zIndex.snackbar,
      }}
    >
      {children}
    </Box>
  );
}

function PromptCard({
  icon,
  badge,
  eyebrow,
  title,
  description,
  hint,
  primaryLabel,
  primaryIcon,
  primaryDisabled,
  primaryLoading,
  secondaryLabel,
  onPrimary,
  onSecondary,
  onClose,
}) {
  const promptId = useId();
  const titleId = `${promptId}-title`;
  const descriptionId = `${promptId}-description`;
  const hintId = hint ? `${promptId}-hint` : undefined;

  return (
    <Fade in timeout={320}>
      <Paper
        role="dialog"
        aria-labelledby={titleId}
        aria-describedby={hint ? `${descriptionId} ${hintId}` : descriptionId}
        aria-busy={primaryLoading || undefined}
        elevation={24}
        sx={{
          width: 1,
          p: 0,
          overflow: 'hidden',
          borderRadius: { xs: 2.5, sm: 3 },
          border: (theme) => `solid 1px ${theme.vars.palette.divider}`,
          boxShadow: (theme) =>
            `0 18px 55px rgb(${theme.vars.palette.grey['500Channel']} / 0.22), ${theme.customShadows.z24}`,
          bgcolor: 'background.paper',
        }}
      >
        <Box
          sx={{
            height: 5,
            background: (theme) =>
              `linear-gradient(90deg, ${theme.vars.palette.primary.main}, ${theme.vars.palette.success.main})`,
          }}
        />

        {primaryLoading && (
          <LinearProgress
            color="success"
            aria-label="در حال اعمال به‌روزرسانی"
            sx={{ height: 2 }}
          />
        )}

        <Box sx={{ p: { xs: 2, sm: 2.75 } }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: { xs: 56, sm: 60 },
                height: { xs: 56, sm: 60 },
                flexShrink: 0,
                display: 'grid',
                borderRadius: 2.25,
                placeItems: 'center',
                color: 'success.dark',
                bgcolor: 'success.lighter',
                position: 'relative',
                boxShadow: (theme) => `inset 0 0 0 1px ${theme.vars.palette.divider}`,
              }}
            >
              <Iconify icon={icon} width={{ xs: 30, sm: 33 }} />
              <Box
                sx={{
                  top: -4,
                  right: -4,
                  width: 14,
                  height: 14,
                  position: 'absolute',
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  border: 'solid 3px',
                  borderColor: 'background.paper',
                }}
              />
            </Box>

            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="overline" sx={{ color: 'primary.main', lineHeight: 1.5 }}>
                  {eyebrow}
                </Typography>
                {badge && (
                  <Chip
                    size="small"
                    color="success"
                    variant="soft"
                    label={badge}
                    icon={<Iconify icon="solar:verified-check-bold" width={16} />}
                    sx={{ height: 25, fontWeight: 700 }}
                  />
                )}
              </Stack>
              <Typography
                id={titleId}
                component="h2"
                variant="h6"
                sx={{ mt: 0.5, lineHeight: 1.45 }}
              >
                {title}
              </Typography>
            </Box>

            <IconButton
              size="small"
              disabled={primaryLoading}
              onClick={onClose}
              aria-label="بستن اعلان"
              sx={{
                mt: -0.75,
                mr: -0.75,
                color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
              }}
            >
              <Iconify icon="mingcute:close-line" />
            </IconButton>
          </Stack>

          <Typography
            id={descriptionId}
            variant="body2"
            sx={{ mt: 1.75, color: 'text.secondary', lineHeight: 1.9 }}
          >
            {description}
          </Typography>

          {hint && (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{
                mt: 1.75,
                p: 1,
                borderRadius: 1.5,
                color: 'text.secondary',
                bgcolor: 'background.neutral',
                border: (theme) => `solid 1px ${theme.vars.palette.divider}`,
              }}
            >
              <Iconify
                icon="solar:shield-check-bold-duotone"
                width={19}
                sx={{ flexShrink: 0, color: 'success.main' }}
              />
              <Typography id={hintId} variant="caption" sx={{ lineHeight: 1.7 }}>
                {hint}
              </Typography>
            </Stack>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2.25 }}>
            <Button
              fullWidth
              size="large"
              variant="contained"
              disabled={primaryDisabled}
              onClick={onPrimary}
              sx={{
                minHeight: 48,
                borderRadius: 1.5,
                fontWeight: 700,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
              startIcon={
                primaryLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <Iconify icon={primaryIcon} />
                )
              }
            >
              {primaryLabel}
            </Button>

            {secondaryLabel && (
              <Button
                size="large"
                color="inherit"
                variant="outlined"
                disabled={primaryLoading}
                onClick={onSecondary}
                sx={{
                  width: { xs: 1, sm: 'auto' },
                  minWidth: { sm: 88 },
                  minHeight: 48,
                  flexShrink: 0,
                  borderRadius: 1.5,
                  fontWeight: 700,
                }}
              >
                {secondaryLabel}
              </Button>
            )}
          </Stack>
        </Box>
      </Paper>
    </Fade>
  );
}
